import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth, apiGet, apiPost } from "@/contexts/AuthContext";
import { Nav } from "@/pages/Dashboard";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle, BookOpen, Target, Lightbulb, MessageCircle, PenLine } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WeekModule() {
  const { weekNum } = useParams();
  const num = parseInt(weekNum);
  const { refreshUser } = useAuth();
  const [week, setWeek] = useState(null);
  const [journal, setJournal] = useState("");
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    apiGet(`${API}/program/weeks/${num}`)
      .then(r => setWeek(r.data))
      .catch(e => {
        if (e.response?.status === 403) setError("This week hasn't been unlocked yet.");
        else setError("Could not load this week.");
      });
  }, [num]);

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await apiPost(`${API}/program/weeks/${num}/complete`, {});
      await refreshUser();
      setWeek(prev => ({ ...prev, is_completed: true }));
      toast.success(num < 6 ? `Week ${num} completed! Week ${num + 1} is now unlocked.` : "Congratulations! You've completed the program.");
    } catch { toast.error("Could not mark as complete."); }
    setCompleting(false);
  };

  const handleJournal = async () => {
    if (!journal.trim()) return;
    setSaving(true);
    try {
      await apiPost(`${API}/journal`, { week_num: num, content: journal });
      toast.success("Journal entry saved.");
      setJournal("");
    } catch { toast.error("Could not save journal entry."); }
    setSaving(false);
  };

  if (error) return (
    <div className="min-h-screen" style={{ background: '#F7F5F0' }}>
      <Nav />
      <div className="flex items-center justify-center py-20">
        <div className="earthy-card text-center max-w-md p-10">
          <h2 className="text-2xl mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>{error}</h2>
          <Link to="/program" className="earthy-btn earthy-btn-primary" data-testid="back-to-program">Back to Program</Link>
        </div>
      </div>
    </div>
  );

  if (!week) return (
    <div className="min-h-screen" style={{ background: '#F7F5F0' }}>
      <Nav />
      <div className="flex items-center justify-center py-20">
        <p className="text-lg" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#5B665E' }}>Loading week {num}...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: '#F7F5F0' }}>
      <Nav />
      <main className="px-6 sm:px-12 py-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8 fade-in">
          <Link to="/program" className="w-10 h-10 rounded-md flex items-center justify-center transition-colors hover:bg-[#EAE6DF]" data-testid="back-btn">
            <ArrowLeft size={20} strokeWidth={1.5} style={{ color: '#5B665E' }} />
          </Link>
          <div>
            <p className="overline mb-1">Week {num} {week.is_completed ? '\u2014 Completed' : ''}</p>
            <h1 className="text-3xl sm:text-4xl" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>{week.title}</h1>
          </div>
        </div>

        {/* Intro */}
        <div className="earthy-card mb-8 fade-in stagger-1" style={{ background: '#EAE6DF', borderColor: 'transparent' }} data-testid="week-intro">
          <p className="text-lg italic mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>{week.subtitle}</p>
          <p className="text-base leading-relaxed" style={{ color: '#5B665E' }}>{week.description}</p>
        </div>

        {/* Topics */}
        <div className="earthy-card mb-8 fade-in stagger-2" data-testid="week-topics">
          <div className="flex items-center gap-3 mb-5">
            <Target size={20} strokeWidth={1.5} style={{ color: '#A67C52' }} />
            <h2 className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>What We Cover</h2>
          </div>
          <ul className="space-y-4">
            {week.key_topics.map((topic, i) => (
              <li key={i} className="flex items-start gap-4">
                <span className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-medium" style={{ background: 'rgba(166,124,82,0.15)', color: '#A67C52' }}>{i + 1}</span>
                <span className="text-base pt-0.5" style={{ color: '#5B665E' }}>{topic}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Exercise */}
        <div className="earthy-card mb-8 fade-in stagger-3" data-testid="week-exercise">
          <div className="flex items-center gap-3 mb-4">
            <BookOpen size={20} strokeWidth={1.5} style={{ color: '#4A5D4E' }} />
            <h2 className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>This Week's Exercise</h2>
          </div>
          <p className="text-base leading-relaxed" style={{ color: '#5B665E' }}>{week.exercise}</p>
        </div>

        {/* Takeaway */}
        <div className="earthy-card mb-8 fade-in stagger-4" style={{ borderLeft: '3px solid #4A5D4E' }} data-testid="week-takeaway">
          <div className="flex items-center gap-3 mb-4">
            <Lightbulb size={20} strokeWidth={1.5} style={{ color: '#A67C52' }} />
            <h2 className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>Key Takeaway</h2>
          </div>
          <p className="text-xl leading-relaxed" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>{week.takeaway}</p>
        </div>

        {/* Discussion */}
        <div className="earthy-card mb-8 fade-in stagger-5" data-testid="week-discussion">
          <div className="flex items-center gap-3 mb-4">
            <MessageCircle size={20} strokeWidth={1.5} style={{ color: '#4A5D4E' }} />
            <h2 className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>Discussion Prompt</h2>
          </div>
          <p className="text-base leading-relaxed mb-5 italic" style={{ color: '#5B665E' }}>{week.discussion_prompt}</p>
          <Link to="/community" className="earthy-btn earthy-btn-secondary text-sm inline-flex items-center gap-1.5" data-testid="go-discuss-btn">
            Join the Discussion <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>

        {/* Journal */}
        <div className="earthy-card mb-8 fade-in stagger-6" data-testid="week-journal">
          <div className="flex items-center gap-3 mb-4">
            <PenLine size={20} strokeWidth={1.5} style={{ color: '#A67C52' }} />
            <h2 className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D3A31' }}>Your Journal</h2>
          </div>
          <p className="text-sm mb-4" style={{ color: '#5B665E' }}>Reflect on this week's content. This is just for you.</p>
          <textarea className="earthy-input min-h-[120px] mb-4 resize-y" placeholder="What came up for you this week?" value={journal} onChange={e => setJournal(e.target.value)} data-testid="journal-textarea" />
          <button onClick={handleJournal} disabled={saving || !journal.trim()} className="earthy-btn earthy-btn-accent text-sm" data-testid="save-journal-btn">
            {saving ? "Saving..." : "Save Entry"}
          </button>
        </div>

        {/* Complete / Nav */}
        <div className="flex flex-wrap items-center justify-between gap-4 fade-in">
          {num > 1 && (
            <Link to={`/program/${num - 1}`} className="earthy-btn earthy-btn-secondary text-sm flex items-center gap-1.5" data-testid="prev-week-btn">
              <ArrowLeft size={14} strokeWidth={1.5} /> Week {num - 1}
            </Link>
          )}
          <div className="flex-1" />
          {!week.is_completed ? (
            <button onClick={handleComplete} disabled={completing} className="earthy-btn earthy-btn-primary text-sm flex items-center gap-2" data-testid="complete-week-btn">
              <CheckCircle size={16} strokeWidth={1.5} /> {completing ? "Completing..." : `Mark Week ${num} Complete`}
            </button>
          ) : num < 6 ? (
            <Link to={`/program/${num + 1}`} className="earthy-btn earthy-btn-primary text-sm flex items-center gap-1.5" data-testid="next-week-btn">
              Week {num + 1} <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          ) : (
            <div className="earthy-card p-4 text-center" data-testid="program-complete-msg">
              <p className="font-medium" style={{ color: '#4A5D4E' }}>Program complete. Well done.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
